import React, { useState } from 'react';
import { siftApiClient } from '../../api/siftApiClient';
import './ChangePasswordModal.css';

interface ChangePasswordModalProps {
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    // Validate password length
    if (newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await siftApiClient.changePassword(currentPassword, newPassword);
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { detail?: string } } };
      setError(axiosError?.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-overlay">
      <div className="change-password-modal glass-card">
        <div className="change-password-header">
          <h3 className="glass-text-primary">Change Password</h3>
          <button className="close-button glass-button" onClick={onClose}>✕</button>
        </div>

        {success ? (
          <div className="change-password-success">
            <span className="success-icon">✓</span>
            <p>Password changed successfully!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="change-password-form">
            {error && (
              <div className="change-password-error">
                {error}
              </div>
            )}

            <div className="form-group">
              <label className="glass-text-secondary">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="glass-input"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="glass-text-secondary">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="glass-input"
                required
                minLength={6}
              />
            </div>

            <div className="form-group">
              <label className="glass-text-secondary">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="glass-input"
                required
                minLength={6}
              />
            </div>

            <div className="form-actions">
              <button type="button" className="glass-button" onClick={onClose}>
                Cancel
              </button>
              <button 
                type="submit" 
                className="glass-button submit-button"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Change Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ChangePasswordModal;
